/** The monthly series, as the chart and its table both read it (ADR-0110).
 *
 * **One derivation, two renderings.** The bars and the "View as table" alternative are written from
 * the `MonthView`s below and from nothing else, so they cannot disagree about a month - the same
 * rule `ChartTable` states for every chart here.
 *
 * Four things this module refuses to do, each because the chart would otherwise tell a story the
 * data does not:
 *
 * - **It never turns a month with no records into a zero.** `state: "no_records"` is carried through
 *   as its own fact. A bare zero reads as "measured, and nil"; an absent bar reads as "not
 *   required". Neither is what a month nobody sent a bill for is.
 * - **It never draws an unassessed scope.** `by_scope[].assessed` is the window's fact and a scope
 *   that is not assessed contributes no segment, however its `kgco2e` is spelled.
 * - **It does the one piece of arithmetic a chart owes its reader, exactly.** The months must add
 *   up to the window total. That is checked with `lib/decimal` (scaled `BigInt`), not `Number`,
 *   because "reconciles" built on float addition is true or false by accident of the binary
 *   expansion. A series that does not reconcile is *refused*, not drawn with a footnote: a chart
 *   whose bars disagree with the total above it is a wrong figure that looks right.
 * - **Geometry is presentation.** `Number` is used only to place a mark. Every value a reader
 *   reads is the server's decimal string, rounded once at display (rule 5).
 */

import type { InventoryMonth, InventoryMonthly } from "../api/queries";
import { sameAmount, sumDecimals } from "../lib/decimal";
import type { EstimateReading } from "../lib/estimate";
import { monthLabel, shortDate, shortMonthYear, share, tonnes } from "../lib/format";

export type ScopeKey = "1" | "2" | "3";
export const SCOPE_KEYS: readonly ScopeKey[] = ["1", "2", "3"];

export interface ScopeCell {
  scope: ScopeKey;
  /** The server's decimal string. Meaningful only when `assessed`. */
  kg: string;
  assessed: boolean;
}

export interface MonthView {
  /** `YYYY-MM`. */
  key: string;
  /** "Apr" - the axis text. */
  short: string;
  /** "2026" - shown on the axis under the first month and under every January. */
  year: string;
  /** "April 2026" - the name a sentence uses. */
  label: string;
  /** The part of the calendar month the series covers: "1 Apr 2026 to 30 Apr 2026". */
  covers: string;
  bucketStart: string;
  bucketEnd: string;
  recorded: boolean;
  isFullMonth: boolean;
  hasEnded: boolean;
  hasEstimate: boolean;
  estimateFigureCount: number;
  estimatedKg: string;
  /** Share of this month's kilograms resting on estimates (figures below grade A), or null when there is none to state. */
  estimateShare: string | null;
  figureCount: number;
  apportionedFigureCount: number;
  totalKg: string;
  scopes: readonly ScopeCell[];
  /** The height of the stack in tonnes, over assessed scopes only, floored at zero for placement. */
  drawnTonnes: number;
}

export interface Reconciliation {
  ok: boolean;
  /** The exact sum of the months, or undefined if some value was not a decimal. */
  monthsKg: string | undefined;
  /** Why it did not reconcile, in a sentence, or null. */
  reason: string | null;
}

export interface MonthlyModel {
  months: readonly MonthView[];
  reconciliation: Reconciliation;
  /** Months that are drawn as data (state `recorded`). */
  recordedCount: number;
  partialCount: number;
  openCount: number;
  estimateMonthCount: number;
  /** Exact sum of `estimated_kgco2e` over the months, or undefined if not summable. */
  estimatedKg: string | undefined;
  /** The reading the figures above are counted in (`lib/estimate`): the legend says it beside its number. */
  estimateReading: EstimateReading;
  apportionedMonthCount: number;
  /** Per scope: whether the window assessed it. A scope the response does not list is not assessed. */
  assessed: Record<ScopeKey, boolean>;
  windowStart: string;
  windowEnd: string;
}

function cellsOf(month: InventoryMonth): ScopeCell[] {
  return SCOPE_KEYS.map((scope) => {
    const found = month.by_scope.find((cell) => cell.scope === scope);
    return { scope, kg: found?.kgco2e ?? "0", assessed: found?.assessed ?? false };
  });
}

export function viewOf(month: InventoryMonth): MonthView {
  const scopes = cellsOf(month);
  const [year = ""] = month.month.split("-");
  const drawnTonnes = scopes.reduce(
    (sum, cell) => (cell.assessed ? sum + Math.max(0, Number(cell.kg) / 1000) : sum),
    0,
  );
  const recorded = month.state === "recorded";
  return {
    key: month.month,
    short: shortMonthYear(month.month).split(" ")[0] ?? month.month,
    year,
    label: monthLabel(month.month),
    covers: `${shortDate(month.bucket_start)} to ${shortDate(month.bucket_end)}`,
    bucketStart: month.bucket_start,
    bucketEnd: month.bucket_end,
    recorded,
    isFullMonth: month.is_full_month,
    hasEnded: month.has_ended,
    hasEstimate: month.has_estimate,
    estimateFigureCount: month.estimate_figure_count,
    estimatedKg: month.estimated_kgco2e,
    estimateShare: month.has_estimate ? share(month.estimated_kgco2e, month.kgco2e) : null,
    figureCount: month.figure_count,
    apportionedFigureCount: month.apportioned_figure_count,
    totalKg: month.kgco2e,
    scopes,
    drawnTonnes: Number.isFinite(drawnTonnes) ? drawnTonnes : 0,
  };
}

/** Whether the months add up to the window total - and to the total the screen shows.
 *
 * Three equalities, exact: the sum of the months equals the response's own total; equals
 * `expectedKg` (the cards' figure, when the caller has one); and each month's stated total equals
 * the sum of its scopes, so a bar is never taller or shorter than the number printed with it. */
export function reconcile(data: InventoryMonthly, expectedKg?: string): Reconciliation {
  const monthsKg = sumDecimals(data.months.map((month) => month.kgco2e));
  if (monthsKg === undefined) {
    return { ok: false, monthsKg, reason: "A month's amount could not be read as a number." };
  }
  for (const month of data.months) {
    const parts = sumDecimals(month.by_scope.map((cell) => cell.kgco2e));
    if (parts === undefined || !sameAmount(parts, month.kgco2e)) {
      return {
        ok: false,
        monthsKg,
        reason: `${monthLabel(month.month)} does not equal the sum of its scopes.`,
      };
    }
  }
  if (!sameAmount(monthsKg, data.kgco2e)) {
    return {
      ok: false,
      monthsKg,
      reason: "The months do not add up to the series' own window total.",
    };
  }
  if (expectedKg !== undefined && !sameAmount(monthsKg, expectedKg)) {
    return {
      ok: false,
      monthsKg,
      reason: "The months do not add up to the window total shown in the cards.",
    };
  }
  return { ok: true, monthsKg, reason: null };
}

export function modelOf(data: InventoryMonthly, expectedKg?: string): MonthlyModel {
  const months = data.months.map(viewOf);
  const assessed: Record<ScopeKey, boolean> = { "1": false, "2": false, "3": false };
  for (const total of data.totals_by_scope) {
    if (total.scope === "1" || total.scope === "2" || total.scope === "3") {
      assessed[total.scope] = total.assessed;
    }
  }
  return {
    months,
    reconciliation: reconcile(data, expectedKg),
    recordedCount: months.filter((month) => month.recorded).length,
    partialCount: months.filter((month) => !month.isFullMonth).length,
    openCount: months.filter((month) => !month.hasEnded).length,
    estimateMonthCount: months.filter((month) => month.hasEstimate).length,
    estimatedKg: sumDecimals(months.map((month) => month.estimatedKg)),
    estimateReading: data.estimate_reading,
    apportionedMonthCount: months.filter((month) => month.apportionedFigureCount > 0).length,
    assessed,
    windowStart: data.period_start,
    windowEnd: data.period_end,
  };
}

/** Whether the chart is drawn at all: the series answered, has at least one month with records,
 * and reconciles. The Analytics note's claim that monthly trends are shown rests on this. */
export function isDrawable(model: MonthlyModel | undefined): boolean {
  return Boolean(model && model.recordedCount > 0 && model.reconciliation.ok);
}

/** A value axis in tonnes: round numbers, at most five intervals, starting at zero. */
export interface Scale {
  max: number;
  step: number;
  ticks: readonly number[];
  decimals: number;
}

export function scaleFor(maxTonnes: number): Scale {
  const top = Number.isFinite(maxTonnes) && maxTonnes > 0 ? maxTonnes : 1;
  const magnitude = 10 ** Math.floor(Math.log10(top));
  const step =
    [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((candidate) => top / candidate <= 5) ??
    10 * magnitude;
  const count = Math.ceil(top / step);
  const ticks = Array.from({ length: count + 1 }, (_, i) => Number((i * step).toPrecision(12)));
  const decimals = step >= 1 && Number.isInteger(step) ? 0 : step >= 1 ? 1 : step >= 0.1 ? 2 : 3;
  return { max: count * step, step, ticks, decimals };
}

/** Tonnes at a precision that does not round a small month to zero: two decimals under 10 t. */
export function shownTonnes(kg: string): string {
  const t = Math.abs(Number(kg) / 1000);
  return tonnes(kg, t < 10 ? 2 : 1);
}
